import React, { Component } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Stack,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Link as MuiLink,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EventIcon from '@mui/icons-material/Event';
import { getPresentations2027, acceptPresentation2027, releasePresentation2027, schedulePresentation2027 } from '../event/apiEvents';
import { isAuthenticated } from '../auth';

// Committees that can accept a presentation.
const COMMITTEES = [
    'Business Strategy',
    'Civil',
    'Electrical',
    'Innovative Solutions',
    'Physical',
    'Technology'
];

// A distinct color per committee for the calendar view.
const COMMITTEE_COLORS = {
    'Business Strategy': '#1565C0', // blue
    'Civil': '#795548',             // brown
    'Electrical': '#F44336',        // red
    'Innovative Solutions': '#00838F', // teal
    'Physical': '#9C27B0',          // purple
    'Technology': '#2E9E6B'         // green
};
const DEFAULT_COMMITTEE_COLOR = '#607D8B'; // blue-grey fallback

// The conference days shown in the calendar view: May 24–27, 2027
// (month is 0-based).
const WEEK_START = new Date(2027, 4, 24);
const WEEK_DAYS = 4;

class Presentations27List extends Component {

    constructor(props) {
        super(props);
        this.state = {
            submissions: [],
            loading: true,
            error: '',
            actionError: '',
            expanded: false,
            view: 'available',        // 'available' | 'approved'
            filterCommittee: 'All',
            acceptSelections: {},     // { [presentationId]: committeeName }
            busyId: '',               // presentation currently being accepted/released
            // Scheduling dialog
            scheduleTarget: null,     // the submission being scheduled
            scheduleValue: '',        // datetime-local string 'YYYY-MM-DDTHH:mm'
            scheduleSaving: false,
            scheduleError: ''
        };
    }

    componentDidMount() {
        this.loadSubmissions();
    }

    loadSubmissions = () => {
        getPresentations2027().then(data => {
            if (!data || data.error) {
                this.setState({
                    loading: false,
                    error: (data && data.error) || 'Could not load submissions.'
                });
            } else {
                this.setState({
                    submissions: Array.isArray(data) ? data : [],
                    loading: false
                });
            }
        });
    };

    handleAccordionChange = (panel) => (event, isExpanded) => {
        this.setState({ expanded: isExpanded ? panel : false });
    };

    handleView = (view) => () => {
        this.setState({ view, filterCommittee: 'All', expanded: false, actionError: '' });
    };

    handleFilter = (committee) => () => {
        this.setState({ filterCommittee: committee, expanded: false });
    };

    handleAcceptSelect = (id) => (event) => {
        this.setState(prev => ({
            acceptSelections: { ...prev.acceptSelections, [id]: event.target.value }
        }));
    };

    // Replace one submission in state with the server's updated copy.
    replaceSubmission = (updated) => {
        this.setState(prev => ({
            submissions: prev.submissions.map(s => (s._id === updated._id ? updated : s))
        }));
    };

    acceptFor = (submission) => () => {
        const id = submission._id;
        const committee = this.state.acceptSelections[id] || submission.committee || COMMITTEES[0];
        const userName = (isAuthenticated() && isAuthenticated().name) || '';

        this.setState({ busyId: id, actionError: '' });
        acceptPresentation2027(id, committee, userName).then(res => {
            if (!res || res.error) {
                // Likely already claimed by another committee — refresh to show truth.
                this.setState({ busyId: '', actionError: (res && res.error) || 'Could not accept this presentation.' });
                this.loadSubmissions();
            } else {
                this.replaceSubmission(res.data);
                this.setState({ busyId: '' });
            }
        });
    };

    releaseFor = (submission) => () => {
        const id = submission._id;
        this.setState({ busyId: id, actionError: '' });
        releasePresentation2027(id, submission.acceptedCommittee).then(res => {
            if (!res || res.error) {
                this.setState({ busyId: '', actionError: (res && res.error) || 'Could not release this presentation.' });
                this.loadSubmissions();
            } else {
                this.replaceSubmission(res.data);
                this.setState({ busyId: '' });
            }
        });
    };

    formatDate = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Readable date + time, e.g. "Mar 12, 2027, 2:30 PM".
    formatDateTime = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    // Convert a Date/ISO string into the 'YYYY-MM-DDTHH:mm' value a
    // datetime-local input expects, in the viewer's local time.
    toLocalInput = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    openSchedule = (submission) => () => {
        this.setState({
            scheduleTarget: submission,
            scheduleValue: this.toLocalInput(submission.scheduledAt),
            scheduleError: ''
        });
    };

    closeSchedule = () => {
        if (this.state.scheduleSaving) return;
        this.setState({ scheduleTarget: null, scheduleValue: '', scheduleError: '' });
    };

    changeScheduleValue = (event) => {
        this.setState({ scheduleValue: event.target.value, scheduleError: '' });
    };

    // Persist a schedule value (or null to clear).
    persistSchedule = (scheduledAtIso) => {
        const target = this.state.scheduleTarget;
        if (!target) return;
        this.setState({ scheduleSaving: true, scheduleError: '' });
        schedulePresentation2027(target._id, scheduledAtIso).then(res => {
            if (!res || res.error) {
                this.setState({ scheduleSaving: false, scheduleError: (res && res.error) || 'Could not save the schedule.' });
            } else {
                this.replaceSubmission(res.data);
                this.setState({ scheduleSaving: false, scheduleTarget: null, scheduleValue: '', scheduleError: '' });
            }
        });
    };

    saveSchedule = () => {
        const { scheduleValue } = this.state;
        if (!scheduleValue) {
            this.setState({ scheduleError: 'Please choose a date and time.' });
            return;
        }
        // datetime-local is local time; toISOString() normalizes to UTC for storage.
        const iso = new Date(scheduleValue).toISOString();
        this.persistSchedule(iso);
    };

    clearSchedule = () => {
        this.persistSchedule(null);
    };

    renderField = (label, value) => {
        if (!value) return null;
        return (
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {value}
                </Typography>
            </Box>
        );
    };

    renderSubmission = (s, i) => {
        const { expanded, busyId, acceptSelections } = this.state;
        const panelId = `panel${s._id || i}`;
        const isOpen = expanded === panelId;
        const speaker = `${s.firstName || ''} ${s.lastName || ''}`.trim();
        const isAccepted = !!(s.acceptedCommittee && s.acceptedCommittee.trim());
        const isBusy = busyId === s._id;

        return (
            <Accordion
                key={s._id || i}
                expanded={isOpen}
                onChange={this.handleAccordionChange(panelId)}
                elevation={2}
                sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: isOpen ? '2px solid #FF6600' : (isAccepted ? '1px solid #4caf50' : 'none'),
                    '&:before': { display: 'none' }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: isOpen ? 'white' : '#003E7E' }} />}
                    sx={{
                        backgroundColor: isOpen ? '#003E7E' : 'rgba(0, 62, 126, 0.05)',
                        color: isOpen ? 'white' : '#003E7E',
                        '&:hover': {
                            backgroundColor: isOpen ? '#00325a' : 'rgba(0, 62, 126, 0.1)'
                        },
                        transition: 'all 0.3s ease'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                        <CampaignIcon />
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {s.proposalTitle || 'Untitled Presentation'}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                {speaker}{s.company ? ` — ${s.company}` : ''}
                            </Typography>
                        </Box>
                        {isAccepted ? (
                            <Chip
                                icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
                                label={`Approved · ${s.acceptedCommittee}`}
                                size="small"
                                sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 600 }}
                            />
                        ) : (
                            s.committee && (
                                <Chip
                                    label={s.committee}
                                    size="small"
                                    sx={{
                                        backgroundColor: isOpen ? 'rgba(255,255,255,0.2)' : '#003E7E',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                            )
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, backgroundColor: 'white' }}>
                    <Grid container spacing={3}>
                        {/* Speaker column */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003E7E', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon fontSize="small" /> Speaker
                            </Typography>
                            {this.renderField('Name', speaker)}
                            {this.renderField('Job Title', s.jobTitle)}
                            {s.company && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Company
                                    </Typography>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> {s.company}
                                    </Typography>
                                </Box>
                            )}
                            {s.email && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Email
                                    </Typography>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        <MuiLink href={`mailto:${s.email}`} underline="hover">{s.email}</MuiLink>
                                    </Typography>
                                </Box>
                            )}
                            {s.linkedIn && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        LinkedIn
                                    </Typography>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LinkedInIcon sx={{ fontSize: 16, color: '#0A66C2' }} />
                                        <MuiLink href={s.linkedIn} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all' }}>
                                            View Profile
                                        </MuiLink>
                                    </Typography>
                                </Box>
                            )}
                            {this.renderField('Speaking Experience', s.experience)}
                            {this.renderField('Bio', s.bio)}
                        </Grid>

                        {/* Presentation column */}
                        <Grid item xs={12} md={8}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#003E7E', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CampaignIcon fontSize="small" /> Presentation
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                {s.committee && <Chip size="small" label={`1st: ${s.committee}`} sx={{ backgroundColor: '#003E7E', color: 'white' }} />}
                                {s.secondCommittee && <Chip size="small" variant="outlined" label={`2nd: ${s.secondCommittee}`} sx={{ borderColor: '#003E7E', color: '#003E7E' }} />}
                                {s.thirdCommittee && <Chip size="small" variant="outlined" label={`3rd: ${s.thirdCommittee}`} sx={{ borderColor: '#003E7E', color: '#003E7E' }} />}
                                {s.sessionFormat && <Chip size="small" label={s.sessionFormat} sx={{ backgroundColor: '#FF6600', color: 'white' }} />}
                                {s.audienceLevel && <Chip size="small" variant="outlined" label={s.audienceLevel} sx={{ borderColor: '#FF6600', color: '#FF6600' }} />}
                            </Stack>

                            {this.renderField('Description / Abstract', s.proposal)}
                            {this.renderField('Key Learning Objectives', s.learningObjectives)}

                            <Divider sx={{ my: 2 }} />

                            {/* Accept / release controls */}
                            {isAccepted ? (
                                <Box sx={{ backgroundColor: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.4)', borderRadius: 2, p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CheckCircleIcon sx={{ color: '#4caf50' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                            Accepted by the {s.acceptedCommittee} committee
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                                        {s.acceptedByUserName ? `Claimed by ${s.acceptedByUserName}` : 'Claimed'}
                                        {s.acceptedAt ? ` on ${this.formatDate(s.acceptedAt)}` : ''}
                                    </Typography>
                                    <Button
                                        onClick={this.releaseFor(s)}
                                        disabled={isBusy}
                                        variant="outlined"
                                        size="small"
                                        startIcon={isBusy ? <CircularProgress size={16} /> : <LockOpenIcon />}
                                        sx={{ color: '#c62828', borderColor: '#c62828', '&:hover': { borderColor: '#8e0000', backgroundColor: 'rgba(198, 40, 40, 0.06)' } }}
                                    >
                                        Release for other committees
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel>Accept for committee</InputLabel>
                                        <Select
                                            value={acceptSelections[s._id] || s.committee || COMMITTEES[0]}
                                            onChange={this.handleAcceptSelect(s._id)}
                                            label="Accept for committee"
                                        >
                                            {COMMITTEES.map(c => (
                                                <MenuItem key={c} value={c}>{c}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        onClick={this.acceptFor(s)}
                                        disabled={isBusy}
                                        variant="contained"
                                        startIcon={isBusy ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
                                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#3d8b40' } }}
                                    >
                                        Accept
                                    </Button>
                                </Box>
                            )}

                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
                                Submitted {this.formatDate(s.createdAt)}
                                {s.archiveConsent ? ' • Archive consent given' : ''}
                            </Typography>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    renderApprovedTable = (rows) => {
        const { busyId } = this.state;
        return (
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table sx={{ minWidth: 700 }} size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#003E7E' }}>
                            {['Presentation', 'Speaker', 'Accepted By', 'Session Format', 'Scheduled', 'Actions'].map(h => (
                                <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((s, i) => {
                            const speaker = `${s.firstName || ''} ${s.lastName || ''}`.trim();
                            const isBusy = busyId === s._id;
                            const scheduled = this.formatDateTime(s.scheduledAt);
                            return (
                                <TableRow
                                    key={s._id || i}
                                    sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 62, 126, 0.03)' } }}
                                >
                                    <TableCell sx={{ fontWeight: 600, color: '#003E7E', maxWidth: 280 }}>
                                        {s.proposalTitle || 'Untitled Presentation'}
                                    </TableCell>
                                    <TableCell>
                                        {speaker}
                                        {s.company && (
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                {s.company}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
                                            label={s.acceptedCommittee}
                                            size="small"
                                            sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell>{s.sessionFormat || '—'}</TableCell>
                                    <TableCell>
                                        {scheduled ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#003E7E', fontWeight: 600 }}>
                                                <EventIcon sx={{ fontSize: 16 }} /> {scheduled}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Not scheduled
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                onClick={this.openSchedule(s)}
                                                variant="contained"
                                                size="small"
                                                startIcon={<EventIcon />}
                                                sx={{ backgroundColor: '#FF6600', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#e55a00' } }}
                                            >
                                                {scheduled ? 'Reschedule' : 'Schedule'}
                                            </Button>
                                            <Tooltip title="Release for other committees">
                                                <span>
                                                    <Button
                                                        onClick={this.releaseFor(s)}
                                                        disabled={isBusy}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ minWidth: 0, color: '#c62828', borderColor: '#c62828', '&:hover': { borderColor: '#8e0000', backgroundColor: 'rgba(198, 40, 40, 0.06)' } }}
                                                    >
                                                        {isBusy ? <CircularProgress size={16} /> : <LockOpenIcon fontSize="small" />}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    renderScheduleDialog = () => {
        const { scheduleTarget, scheduleValue, scheduleSaving, scheduleError } = this.state;
        const open = !!scheduleTarget;
        const existing = scheduleTarget && scheduleTarget.scheduledAt;

        return (
            <Dialog open={open} onClose={this.closeSchedule} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ backgroundColor: '#003E7E', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon /> Schedule Presentation
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {scheduleTarget && (
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, mt: 1 }}>
                            {scheduleTarget.proposalTitle}
                        </Typography>
                    )}
                    {scheduleTarget && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                            {scheduleTarget.acceptedCommittee} committee
                        </Typography>
                    )}
                    {scheduleError && <Alert severity="error" sx={{ mb: 2 }}>{scheduleError}</Alert>}
                    <TextField
                        type="datetime-local"
                        label="Date &amp; Time"
                        value={scheduleValue}
                        onChange={this.changeScheduleValue}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Box>
                        {existing && (
                            <Button onClick={this.clearSchedule} disabled={scheduleSaving} color="error">
                                Clear
                            </Button>
                        )}
                    </Box>
                    <Box>
                        <Button onClick={this.closeSchedule} disabled={scheduleSaving} sx={{ color: '#003E7E' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={this.saveSchedule}
                            disabled={scheduleSaving}
                            variant="contained"
                            startIcon={scheduleSaving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                            sx={{ backgroundColor: '#FF6600', ml: 1, '&:hover': { backgroundColor: '#e55a00' } }}
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        );
    };

    committeeColor = (name) => COMMITTEE_COLORS[name] || DEFAULT_COMMITTEE_COLOR;

    // True if the given date value falls on the same local calendar day as `day`.
    isSameDay = (value, day) => {
        if (!value) return false;
        const d = new Date(value);
        if (isNaN(d.getTime())) return false;
        return d.getFullYear() === day.getFullYear()
            && d.getMonth() === day.getMonth()
            && d.getDate() === day.getDate();
    };

    renderWeekCalendar = () => {
        const scheduled = this.state.submissions.filter(s => s.scheduledAt);

        // Build the 7 day columns for the conference week.
        const days = [];
        for (let i = 0; i < WEEK_DAYS; i++) {
            days.push(new Date(WEEK_START.getFullYear(), WEEK_START.getMonth(), WEEK_START.getDate() + i));
        }

        const inWeek = scheduled.filter(s => days.some(day => this.isSameDay(s.scheduledAt, day)));
        const outsideWeek = scheduled.length - inWeek.length;

        const weekLabel = `${WEEK_START.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} – ${days[WEEK_DAYS - 1].toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;

        return (
            <Box sx={{ mt: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EventIcon sx={{ color: '#003E7E' }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E' }}>
                        Conference Schedule
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {weekLabel} · {inWeek.length} scheduled
                    {outsideWeek > 0 ? ` · ${outsideWeek} scheduled on other dates` : ''}
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                        gap: 1.5
                    }}
                >
                    {days.map((day, idx) => {
                        const dayEvents = scheduled
                            .filter(s => this.isSameDay(s.scheduledAt, day))
                            .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

                        return (
                            <Paper
                                key={idx}
                                elevation={2}
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 160,
                                    border: dayEvents.length ? '1px solid rgba(0, 62, 126, 0.25)' : '1px solid #eee'
                                }}
                            >
                                <Box sx={{ backgroundColor: '#003E7E', color: 'white', px: 1.5, py: 1, textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {day.toLocaleDateString(undefined, { weekday: 'short' })}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                        {day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {dayEvents.length === 0 ? (
                                        <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', mt: 2 }}>
                                            —
                                        </Typography>
                                    ) : (
                                        dayEvents.map((s, i) => {
                                            const color = this.committeeColor(s.acceptedCommittee);
                                            return (
                                                <Box
                                                    key={s._id || i}
                                                    sx={{
                                                        borderLeft: `3px solid ${color}`,
                                                        backgroundColor: `${color}14`,
                                                        borderRadius: 1,
                                                        px: 1,
                                                        py: 0.75
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color }}>
                                                            {new Date(s.scheduledAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                                                        </Typography>
                                                        {s.acceptedCommittee && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ fontWeight: 600, color, backgroundColor: `${color}22`, px: 0.6, borderRadius: 0.5, lineHeight: 1.5 }}
                                                            >
                                                                {s.acceptedCommittee}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#003E7E', display: 'block', lineHeight: 1.25 }}>
                                                        {s.proposalTitle || 'Untitled'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>

                {/* Committee color legend */}
                <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                    {COMMITTEES.map(c => (
                        <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: this.committeeColor(c) }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c}</Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    };

    render() {
        const { submissions, loading, error, actionError, view, filterCommittee } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                    <CircularProgress size={48} />
                    <Typography variant="body1" color="text.secondary">Loading submissions...</Typography>
                </Box>
            );
        }

        if (error) {
            return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
        }

        if (!submissions.length) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CampaignIcon sx={{ fontSize: 56, color: '#003E7E', opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No presentations have been submitted yet.</Typography>
                </Box>
            );
        }

        const available = submissions.filter(s => !(s.acceptedCommittee && s.acceptedCommittee.trim()));
        const approved = submissions.filter(s => s.acceptedCommittee && s.acceptedCommittee.trim());
        const base = view === 'available' ? available : approved;

        // Committee filter: for available, match any of the three requested
        // choices; for approved, match the accepting committee.
        const filtered = filterCommittee === 'All'
            ? base
            : base.filter(s => view === 'available'
                ? [s.committee, s.secondCommittee, s.thirdCommittee].includes(filterCommittee)
                : s.acceptedCommittee === filterCommittee);

        return (
            <Box>
                {/* View toggle: Available vs Approved */}
                <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        onClick={this.handleView('available')}
                        variant={view === 'available' ? 'contained' : 'outlined'}
                        sx={{
                            backgroundColor: view === 'available' ? '#003E7E' : 'transparent',
                            color: view === 'available' ? 'white' : '#003E7E',
                            borderColor: '#003E7E',
                            '&:hover': { backgroundColor: view === 'available' ? '#00325a' : 'rgba(0, 62, 126, 0.08)' }
                        }}
                    >
                        Available ({available.length})
                    </Button>
                    <Button
                        onClick={this.handleView('approved')}
                        variant={view === 'approved' ? 'contained' : 'outlined'}
                        startIcon={<CheckCircleIcon />}
                        sx={{
                            backgroundColor: view === 'approved' ? '#4caf50' : 'transparent',
                            color: view === 'approved' ? 'white' : '#2e7d32',
                            borderColor: '#4caf50',
                            '&:hover': { backgroundColor: view === 'approved' ? '#3d8b40' : 'rgba(76, 175, 80, 0.08)' }
                        }}
                    >
                        Approved ({approved.length})
                    </Button>
                </Stack>

                {actionError && (
                    <Alert severity="warning" sx={{ mb: 2 }} onClose={() => this.setState({ actionError: '' })}>
                        {actionError}
                    </Alert>
                )}

                {/* Committee filter */}
                <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    {['All', ...COMMITTEES].map(opt => (
                        <Chip
                            key={opt}
                            label={opt}
                            onClick={this.handleFilter(opt)}
                            variant={filterCommittee === opt ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                backgroundColor: filterCommittee === opt ? '#FF6600' : 'transparent',
                                color: filterCommittee === opt ? 'white' : '#003E7E',
                                borderColor: '#003E7E',
                                '&:hover': {
                                    backgroundColor: filterCommittee === opt ? '#e55a00' : 'rgba(0, 62, 126, 0.08)'
                                }
                            }}
                        />
                    ))}
                </Stack>

                <Typography variant="body2" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                    {filtered.length} {filtered.length === 1 ? 'presentation' : 'presentations'}
                    {view === 'available' ? ' available' : ' approved'}
                    {filterCommittee !== 'All' ? ` · ${filterCommittee}` : ''}
                </Typography>

                {filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="body1" color="text.secondary">
                            {view === 'available' ? 'No available presentations match this filter.' : 'No approved presentations match this filter.'}
                        </Typography>
                    </Box>
                ) : view === 'approved' ? (
                    this.renderApprovedTable(filtered)
                ) : (
                    filtered.map((s, i) => this.renderSubmission(s, i))
                )}

                {view === 'approved' && this.renderWeekCalendar()}

                {this.renderScheduleDialog()}
            </Box>
        );
    }
}

export default Presentations27List;
